
import React from 'react';
import { Edit, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import StatusBadge from '@/components/ui/StatusBadge';
import { SubscriptionService } from '@/lib/data';

interface ServicesTableProps {
  groupedServices: Record<string, { service: SubscriptionService | null; children: SubscriptionService[] }>;
  handleEditService: (service: SubscriptionService) => void;
  handleDeleteService: (serviceId: string) => Promise<void>;
  isLoading: boolean;
}

const ServicesTable: React.FC<ServicesTableProps> = ({
  groupedServices,
  handleEditService,
  handleDeleteService,
  isLoading
}) => {
  const formatDate = (date: string | null) => {
    if (!date) return '-';
    return format(new Date(date), 'PP');
  };

  const formatPrice = (price: number | null, defaultPrice: number) => {
    if (price === null) return `$${defaultPrice.toFixed(2)}`;
    return `$${price.toFixed(2)}`;
  };

  return (
    <div className="border rounded-md overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Service</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="hidden md:table-cell">Registration</TableHead>
            <TableHead className="hidden md:table-cell">Price</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Object.values(groupedServices).map(({ service, children }) => (
            <React.Fragment key={service?.service_id || children[0].service_id}>
              {service && (
                <TableRow>
                  <TableCell className="font-medium">{service.service.service_name}</TableCell>
                  <TableCell>
                    <StatusBadge status={service.status} />
                  </TableCell>
                  <TableCell className="hidden md:table-cell">{formatDate(service.registration_date)}</TableCell>
                  <TableCell className="hidden md:table-cell">{formatPrice(service.price, service.service.default_price)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEditService(service)}
                      disabled={isLoading}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteService(service.service_id)}
                      disabled={isLoading}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              )}
              {children.map(childService => (
                <TableRow key={childService.service_id}>
                  <TableCell className="pl-6">
                    <span className="text-sm text-muted-foreground">└ </span>
                    {childService.service.service_name}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={childService.status} />
                  </TableCell>
                  <TableCell className="hidden md:table-cell">{formatDate(childService.registration_date)}</TableCell>
                  <TableCell className="hidden md:table-cell">{formatPrice(childService.price, childService.service.default_price)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEditService(childService)}
                      disabled={isLoading}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteService(childService.service_id)}
                      disabled={isLoading}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default ServicesTable;
